<template>
  <div>
    <section v-if="experience === null">
      <div class="p-5" style="height: 100vh">
        <loader/>
      </div>
    </section>
    <section v-else>
      <div v-if="experience.status === 1">
        <section class="py-4 px-3 p-lg-4">
          <div class="row row-experience-detail">
            <!-- Share and Fav buttons -->
            <div class="col-12 d-flex justify-content-start align-items-center">
              <div class="flex-fill">
                <router-link :to="`/special-services/search`" class="text-info">
                  {{ $t('message.home.specialty_services') }}
                </router-link>
                <div class="d-inline-block px-2"><i class="fal fa-angle-right"></i></div>
                <router-link :to="`/special-services/search?service_id=${experience.service_id}`" class="text-info">
                  <span v-html="$options.filters.serviceParentName(experience.service_id, globalState.services)"></span>
                </router-link>
              </div>
              <div class="flex-fill text-right">
                <button class="btn btn-light btn-sm" @click="openShareProfileModal()">
                  <i class="fal fa-share-square nav-icon"></i>&nbsp;{{ $t('message.professional.menu.share') }}
                </button>
                <div class="d-inline-block">
                  <template v-if="experience && (!experience.is_favorited_experience)">
                    <button class="btn btn-light btn-sm" @click="updateFavExperience(true)">
                      <i class="fal fa-heart nav-icon"></i>&nbsp;{{ $t('message.professional.menu.save') }}
                    </button>
                  </template>
                  <template v-else>
                    <button class="btn btn-light btn-sm" @click="updateFavExperience(false)">
                      <i class="fas fa-heart nav-icon text-danger"></i>&nbsp;{{ $t('message.professional.menu.saved') }}
                    </button>
                  </template>
                </div>
              </div>
            </div>
            <!-- Experience photos -->
            <div class="col-12 pt-3 pb-4">
              <experience-photos :experience="experience"/>
            </div>
            <div class="col-12 col-lg-8 pr-lg-4"
                 id="experience-content"
                 v-if="experience.status === 1">
              <!-- Experience title -->
              <section>
                <div class="experience-category">
                  <span v-html="$options.filters.serviceParentName(experience.service_id, globalState.services)"></span>
                  •
                  {{ globalState.cities[experience.province_id] | multiLangName }}
                </div>
                <div class="pt-2 experience-title">{{ experience.title }}</div>

                <div class="d-flex justify-content-start align-items-center pt-4">
                  <div>
                    <div class="position-relative">
                      <img :src="experience.professional.picture"
                           :alt="experience.professional.name"
                           class="img-fluid rounded-circle" width="64">
                      <div class="position-absolute badge-excellent">
                        <router-link to="/excellent-provider" @click.prevent target="_blank"
                                     v-if="experience.professional.is_excellent === 1">
                          <img src="/static/img/ic_badge_excellent.png"
                               v-b-popover.hover.top="$i18n.t('message.professional.excellent_provider_detail', {name: experience.professional.firstname})"
                               :title="$i18n.t('message.professional.excellent_provider_title')"
                               width="26">
                        </router-link>
                      </div>
                    </div>
                  </div>
                  <div class="flex-grow-1 pl-3">
                    <div class="d-flex">
                      <div class="flex-fill flex-grow-1">
                        <h4 class="mb-0">{{ experience.professional.name | firstName }}</h4>
                      </div>
                      <div class="flex-fill text-right">
                        <div v-if="experience.avg_rating">
                          <i class="fas fa-star -rating-star mr-0"></i> {{ Number(experience.avg_rating).toFixed(1) }}
                          ·
                          <a class="text-primary-link"
                             href="#"
                             v-scroll-to="{el:'#experience-reviews', offset: -240}">
                            {{ experience.num_rating }} {{ $tc('message.professional.review', experience.num_rating) }}
                          </a>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </section>

              <!-- Experience description -->
              <section class="pt-5" v-if="experience.description">
                <h3 class="booking-service-title">{{ $t('message.experience.service_description') }}</h3>
                <div class="text-long" ref="description" style="white-space: break-spaces;">
                  <span v-html="$options.filters.shortText(experience.description, descriptionLength)"></span>
                  <a href="" class="text-primary-link" 
                    @click.prevent="showMore('description')" 
                    v-if="experience.description.length > descriptionLength">{{ $t('message.professional.more') }}</a>
                </div>
                <div class="mt-3" v-if="experience.description_image_url">
                  <a href="" @click.prevent="openLightbox(experience.description_image_url)">
                    <img :src="experience.description_image_url" class="img-fluid" />
                  </a>
                </div>
                <div class="py-3">
                  <hr>
                </div>
              </section>

              <!-- Experience remark -->
              <section v-if="experience.remark">
                <h3 class="booking-service-title">{{ $t('message.experience.what_else_you_should_know') }}</h3>
                <div class="text-long" ref="remark" style="white-space: break-spaces;">
                  <span v-html="$options.filters.shortText(experience.remark, descriptionLength)"></span>
                  <a href="" class="text-primary-link" 
                    @click.prevent="showMore('remark')"
                    v-if="experience.remark.length > descriptionLength">{{
                      $t('message.professional.more') }}</a>
                </div>
                <div class="mt-3" v-if="experience.remark_image_url">
                  <a href="" @click.prevent="openLightbox(experience.remark_image_url)">
                    <img :src="experience.remark_image_url" class="img-fluid" />
                  </a>
                </div>
                <div class="py-3">
                  <hr>
                </div>
              </section>

              <!-- Experience provider -->
              <section v-if="experience.about">
                <h3 class="booking-service-title">{{ $t('message.experience.about_your_provider') }}</h3>
                <div class="text-long" ref="about" style="white-space: break-spaces;">
                  <span v-html="$options.filters.shortText(experience.about, descriptionLength)"></span>
                  <a href="" class="text-primary-link" 
                    @click.prevent="showMore('about')"
                    v-if="experience.about.length > descriptionLength">
                    {{ $t('message.professional.more') }}
                  </a>
                </div>
                <div class="py-3">
                  <hr>
                </div>
              </section>

              <!-- Experience reviews -->
              <section>
                <experience-reviews/>
              </section>
            </div>
            <div class="col-lg-4" v-if="experience.status === 1 && $mq.desktop">
              <div v-sticky sticky-offset="{top: 120, bottom: 10}">
                <div class="card" v-if="experience.service && Number(experience.service.is_service_request) !== 1">
                  <div class="card-body px-1 py-4">
                    <book-experience-form
                      :professional="experience.professional"
                      :experience="experience"
                      :service="selectedService"
                      :from="'experienceDetail'"
                      class="form-book-cleaner"/>
                  </div>
                </div>

                <div class="card" v-if="experience.service && Number(experience.service.is_service_request) === 1">
                  <div class="card-body">
                    <h4 class="text-left">
                      {{ $t('message.booking.new_booking.book_a_service') }}:
                      <span v-html="$options.filters.serviceParentName(experience.service_id, globalState.services)"></span>
                    </h4>
                    <div class="pb-3">
                      {{ $t('message.experience.send_request_remark01') }}<br>
                      {{ $t('message.experience.send_request_remark02') }}
                    </div>
                    <button class="btn btn-info btn-block" @click.prevent="goToBookingPage">
                      {{ $t('message.experience.evaluate') }}
                    </button>
<!--                    <router-link
                      :to="{path: '/service-requests/booking', query: {
                        service_id: experience.service.id,
                        experience_id: experience.id }}"
                      class="btn btn-info btn-block">
                      {{ $t('message.experience.evaluate') }}
                    </router-link>-->
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
      <div class="p-5" v-else>
        <h3 class="text-center">
          <i class="fas fa-exclamation-triangle -status-pending"></i>
          {{ $t('message.professional.pause_service') }}
        </h3>
      </div>
      <book-experience-modal
        :professional="experience.professional"
        :experience="experience"
        v-if="experience && experience.status === 1 && !$mq.desktop"/>
    </section>

    <share-profile-modal ref="shareProfileModal"/>
    <Tinybox v-model="indexImage" :images="images" loop no-thumbs style="z-index: 9999;" />

    <!-- Unvailable service area modal -->
    <unavailable-service-area-modal ref="unavailableServiceAreaModal"/>
  </div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import BookExperienceForm from './components/BookExperienceForm'
import BookExperienceModal from './components/BookExperienceModal'
import ExperiencePhotos from './components/ExperiencePhotos'
import ExperienceReviews from './components/ExperienceReviews'
import Loader from '../loader/Loader'
import ProfessionalPicture from '../professional/components/ProfessionalPicture'
import ShareProfileModal from '../professional/components/ShareProfileModal'
import UnavailableServiceAreaModal from './components/UnavailableServiceAreaModal'
import userEventLogHelpers from '../../helpers/userEventLogHelpers'
import insiderHelpers from '../../helpers/insiderHelpers'

export default {
  components: {
    BookExperienceForm,
    BookExperienceModal,
    ExperienceReviews,
    ExperiencePhotos,
    ProfessionalPicture,
    ShareProfileModal,
    UnavailableServiceAreaModal,
    Loader
  },
  data () {
    return {
      experienceId: this.$route.params.id,
      selectedService: null,
      descriptionLength: 400,
      pageTitle: 'untitled',
      canonicalHref: null,
      meta: [],
      indexImage: null,
      images: null
    }
  },
  metaInfo () {
    return {
      title: this.pageTitle,
      htmlAttrs: {
        lang: this.$i18n.locale
      },
      meta: this.meta,
      link: [
        { rel: 'canonical', href: this.canonicalHref }
      ]
    }
  },
  computed: {
    ...mapState({
      checkFavoriteTempData: state => state.auth.checkFavoriteTempData
    }),
    ...mapGetters({
      globalState: 'globalVal/getState',
      experience: 'experience/getExperience',
      user: 'auth/getUser'
    })
  },
  watch: {
    experience () {
      if (this.experience) {
        console.log('initial')
        this.canonicalHref = `https://beneat.co/special-services/${this.experience.id}`
        this.pageTitle = this.experience.title
        if (this.experience.service && this.experience.province) {
          this.pageTitle += ` | ${this.experience.service.name_th}ใน${this.experience.province.name_th}`
        }
        this.pageTitle += ' | BeNeat.co'

        this.meta = [
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1' },
          { vmid: 'description', name: 'description', content: this.experience.description },
          { name: 'robots', content: 'index,follow' },
          { property: 'og:title', content: this.pageTitle },
          {
            property: 'og:site_name',
            content: 'BeNeat จองบริการแม่บ้านออนไลน์ แม่บ้านรายวัน แม่บ้านรายชั่วโมง ได้สะดวก รวดเร็ว พร้อมรับประกันคุณภาพ'
          },
          { property: 'og:description', content: this.experience.description },
          { property: 'og:type', content: 'profile' },
          { property: 'og:url', content: `https://beneat.co/special-services/${this.experience.id}` },
          { property: 'og:image', content: this.experience.cover_photo }
        ]
      }
    },
    user (newValue) {
      if (newValue) {
        // ตั้งค่ากิจกรรมที่ทำก่อนจะ login
        this.setTempActivities()
      }
    }
  },
  methods: {
    openUnavailableServiceAreaModal () {
      this.$refs.unavailableServiceAreaModal.openModal()
    },
    openLightbox (imageUrl) {
      this.images = [imageUrl]
      this.indexImage = 0
    },
    goToBookingPage () {
      const queryParams = {
        service_id: this.experience.service.id,
        experience_id: this.experience.id
      }

      if (this.$route.query.admin_id) {
        queryParams.admin_id = this.$route.query.admin_id
      }

      this.$router.push({
        path: '/service-requests/booking',
        query: { ...queryParams }
      })
    },
    setTempActivities () {
      console.log('setTempActivities', this.checkFavoriteTempData, this.user)
      // check favorite experience
      if (this.checkFavoriteTempData && this.user) {
        const data = { ...this.checkFavoriteTempData, user_id: this.user.id }
        this.$store.dispatch('user/ADD_USER_EXPERIENCE', data)
        this.$store.commit('experience/UPDATE_FAVORITE_EXPERIENCE', data.status)
        // clear value
        this.$store.commit('auth/SET_CHECK_FAVORITE_EXPERIENCE', null)
        this.$forceUpdate()
      }
    },
    initialData () {
      this.$store.dispatch('experience/LOAD_EXPERIENCE_DETAIL', this.experienceId)
    },
    showMore (content) {
      if (content === 'about') {
        this.$refs.about.innerHTML = this.experience.about
      } else if (content === 'description') {
        this.$refs.description.innerHTML = this.experience.description
      } else if (content === 'remark') {
        this.$refs.remark.innerHTML = this.experience.remark
      }
    },
    firstName (professional) {
      return this.$options.filters.firstName(professional.full_name)
    },
    openShareProfileModal () {
      this.$refs.shareProfileModal.openModal(this.experience, 'experience')
    },
    updateFavExperience (status) {
      const submitData = {
        experience_id: this.experience.id
      }
      if (this.user) {
        submitData.user_id = this.user.id
        if (status) {
          this.$store.dispatch('user/ADD_USER_EXPERIENCE', submitData)
        } else {
          this.$store.dispatch('user/DELETE_USER_EXPERIENCE', submitData)
        }
        this.$store.commit('experience/UPDATE_FAVORITE_EXPERIENCE', status)
      } else {
        // เรียก function ใน App.vue ซึ่งจะเป็น root
        this.$root.$children[0].openModal('login')
        submitData.status = true
        this.$store.commit('auth/SET_CHECK_FAVORITE_EXPERIENCE', submitData)
      }
      this.$forceUpdate()
    }
  },
  mounted () {
    this.initialData()
    if (this.$mq.phone) {
      this.descriptionLength = 200
    }
    // Save user event log
    userEventLogHelpers.logEvent('view_experience', {
      experience_id: this.experienceId
    })
    // Insider event: special service page view event
    insiderHelpers.specialServicePageViewEvent(this.user, this.experienceId)
  }
}
</script>

<style scoped lang="scss">
  img.badge-excellent-experience {
    position: absolute;
    bottom: 0;
    right: 25px;
  }
</style>



// WEBPACK FOOTER //
// src/components/experience/ExperienceDetail.vue